import React, { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import api from "../lib/api";

/** Reusable searchable subcontractor picker — searches the backend live as
 *  the user types (never a capped/preloaded list), so results are always
 *  complete no matter how many subcontractors exist in the register.
 *
 *  Bug fixed: several "Send to Subcontractor" style modals preloaded the
 *  ENTIRE subcontractor list once on mount via a plain `api.get("/subcontractors")`
 *  (no `limit` param → backend default of 50) and rendered it into a native
 *  `<datalist>` — once the register grew past 50 records, roughly half of
 *  them silently never appeared. Searching on demand removes the cap
 *  entirely and matches the established Equipment/Manufacturer/Issue-Number
 *  typeahead UX already used on Receiving.
 *
 *  - `value` / `onChange` — the free-typed subcontractor NAME, always kept
 *    in sync (what gets submitted for the name-only flows).
 *  - `onPick(subcontractor)` — fired only when a suggestion is actually
 *    clicked; use it to also capture `subcontractor_id` where a real
 *    register link is required. Typing without picking a suggestion never
 *    invents an id — same guarantee a native `<select>` gave.
 *  - `allowFreeText` (default true) — only affects the "no matches" hint
 *    wording; typing is never blocked either way.
 */
export function SubcontractorTypeahead({
  value, onChange, onPick, placeholder, testid, activeOnly = true, allowFreeText = true,
}) {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const v = (value || "").trim();
    if (v.length < 1) { setMatches([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/subcontractors", {
          params: { search: v, active_only: activeOnly, limit: 20, sort_by: "name", sort_dir: "asc" },
        });
        setMatches(r.data?.items || []);
      } catch { setMatches([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [value, activeOnly]);

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const v = (value || "").trim();
  const hasResults = open && v.length >= 1;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div className="input" style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px" }}>
        <Search size={13} color="var(--text-3)" />
        <input
          style={{ border: "none", outline: "none", width: "100%", background: "none", fontSize: 13 }}
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          data-testid={testid}
        />
      </div>
      {hasResults && (
        <div className="card" style={{ position: "absolute", zIndex: 20, top: 40, left: 0, right: 0, maxHeight: 240, overflowY: "auto", padding: 4 }}
          data-testid={`${testid}-dropdown`}>
          {loading && <div className="t-mut" style={{ fontSize: 12, padding: "8px 10px" }}>Searching…</div>}
          {!loading && matches.length === 0 && (
            <div className="t-mut" style={{ fontSize: 12, padding: "8px 10px" }} data-testid={`${testid}-no-matches`}>
              No matching subcontractor for &quot;{v}&quot;{allowFreeText ? " — free text is fine." : "."}
            </div>
          )}
          {!loading && matches.map((s) => (
            <button key={s.id} type="button" className="nav-item"
              style={{ color: "var(--text)", borderRadius: 6, width: "100%", textAlign: "left", padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              onClick={() => { onChange(s.name); onPick && onPick(s); setOpen(false); }}
              data-testid={`${testid}-opt-${s.id}`}>
              <span className="t-strong" style={{ fontSize: 13 }}>{s.name}</span>
              {s.vendor_number && <span className="mono t-mut" style={{ fontSize: 11 }}>{s.vendor_number}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
